/**
 * External dependencies
 */
import classnames from 'classnames';
import { isUndefined, pickBy, get, isEmpty } from 'lodash';
import { Swiper, SwiperSlide } from 'swiper/react';

/**
 * WordPress dependencies.
 */
import { useEffect, RawHTML } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { RichText } from '@wordpress/block-editor';
import { compose } from '@wordpress/compose';
import { withSelect, withDispatch } from '@wordpress/data';
import { QueryControls, Spinner } from '@wordpress/components';
import { dateI18n, format, __experimentalGetSettings } from '@wordpress/date';
import { store as coreStore } from '@wordpress/core-data';
import { applyFilters } from '@wordpress/hooks';

/**
 * Internal dependencies
 */
import Inspector from './inspector';
import BaseWrapper from '../../components/base-wrapper';
import blockInfo from './constants';
const { hookPrefix } = blockInfo;

// import Swiper core and required modules
import SwiperCore, { Pagination, Navigation, Autoplay } from 'swiper/core';

// install Swiper modules
SwiperCore.use( [ Pagination, Navigation, Autoplay ] );

function DisplayPost( props ) {
	const { post, setAttributes, attributes } = props;
	const { postLink, excerptLength, displayFeaturedImage, displayPostDate, displayPostContent, displayPostLink, displayPostAuthor, displayPostCategories } = attributes;
	const { link, title } = post;
	const dateFormat = __experimentalGetSettings().formats.date; // eslint-disable-line no-restricted-syntax
	let excerpt = post.excerpt.rendered;
	if ( post.excerpt.raw === '' ) {
		excerpt = post.content.raw;
	}
	const excerptElement = document.createElement( 'div' );
	excerptElement.innerHTML = excerpt;
	excerpt = excerptElement.textContent || excerptElement.innerText || '';

	// Featured image.
	const featuredImageUrl = post.featured_media_object ? post.featured_media_object.source_url : null;
	return <div className="gutenify-post-carousel-item-inner-wrapper">
		{ displayFeaturedImage && featuredImageUrl &&
		<div className="gutenify-post-carousel-thumb">
			<a className="image-hover-zoom" href={ link } onClick={ ( event ) => event.preventDefault() }>
				<img src={ featuredImageUrl } alt={ title.rendered } />
			</a>
		</div>
		}
		<div className="gutenify-post-carousel-text-content">
			<h3 className="gutenify-post-carousel-title">
				<a href={ link } onClick={ ( event ) => event.preventDefault() } rel="bookmark">
					<RawHTML key="html">{ title.rendered }</RawHTML>
				</a>
			</h3>
			<div className="gutenify-post-carousel-meta">
				{ displayPostDate && post.date_gmt &&
				<span className="posted-on"><time dateTime={ format( 'c', post.date_gmt ) } className="entry-date gutenify-posts-date">
					{ dateI18n( dateFormat, post.date_gmt ) }
				</time></span>
				}
				{ ! isEmpty( post.authorInfo ) && displayPostAuthor && <span className="byline"><a href={ link } onClick={ ( event ) => event.preventDefault() } rel="author">{ post.authorInfo.name }</a></span> }
				{ ! isEmpty( post.categories ) && displayPostCategories && <span className="cat-links"><>{ post.categories.map( ( term ) => {
					return <a href={ link } onClick={ ( event ) => event.preventDefault() } rel="category tag" key={ term.id }>{ term.name }</a>;
				} ) }</></span> }
			</div>
			{ displayPostContent && <>
				<div className="entry-summary">
					<RawHTML
						key="html"
					>
						{ excerptLength < excerpt.trim().split( ' ' ).length
							? excerpt.trim().split( ' ', excerptLength ).join( ' ' ) + '…'
							: excerpt.trim().split( ' ', excerptLength ).join( ' ' ) }
					</RawHTML>
					{ displayPostLink && <RichText
						tagName="a"
						className="wp-block-gutenify-posts__more-link"
						onChange={ ( newPostLink ) => setAttributes( { postLink: newPostLink } ) }
						value={ postLink }
						placeholder={ __( 'Read more', 'gutenify' ) }
						multiline={ false }
						withoutInteractiveFormatting={ false }
						isSelected={ false }
					/> }
				</div>

			</> }

		</div>
	</div>;
}

const Edit = ( props ) => {
	const { isSelected, className, attributes, latestPosts, selectBlock } = props;
	const { columns, hasPagination, hasNavigation, spaceBetween, layout, blockClientId } = attributes;
	// useEffect( () => {
	// 	updateBlockStyle( {}, props );
	// }, [] );
	let wrapperClasses = [
		className,
		`gutenify-post-carousel-${ layout }`,
		`gutenify-section-${ blockClientId }`,
	];

	wrapperClasses = classnames( applyFilters( `gutenify--${ hookPrefix }--wrapper-class`, wrapperClasses, props ) );

	const hasPosts = Array.isArray( latestPosts ) && latestPosts.length;

	return <div className={ wrapperClasses }>
		{ isSelected && <Inspector { ...props } /> }
		<BaseWrapper { ...props }>
			{ ! hasPosts && <Spinner /> }
			{ hasPosts && <Swiper
				spaceBetween={ spaceBetween }
				slidesPerView={ columns }
				loop={ true }
				navigation={ hasNavigation }
				pagination={ hasPagination }
				scrollbar={ { draggable: true } }
				onClick={ () => selectBlock() }
				autoplay={ false }
				className="gutenify-post-carousel"
				allowTouchMove={ false }
				rebuildOnUpdate={ true }
			>
				{ latestPosts.map( ( post ) => {
					return <SwiperSlide className="gutenify-post-carousel-item gutenify-post-carousel-item-wrapper inner-block-shadow" key={ post.id || post.link }>
						<DisplayPost post={ post } { ...props } />
					</SwiperSlide>;
				} ) }
			</Swiper> }
		</BaseWrapper>
	</div>;
};

export default compose( [
	withSelect( ( select, props ) => {
		const { query } = props.attributes;
		const { getEntityRecords, getMedia, getUser } = select( coreStore );
		const { getCurrentPost } = select( 'core/editor' );

		const useUpdatedQueryControls = QueryControls.toString().includes( 'selectedCategories' );
		const currentPost = getCurrentPost();

		const newQuery = {
			...query,
		};

		const updatedQuery = () => {
			const catIds = newQuery.selectedCategories && newQuery.selectedCategories.length > 0
				? newQuery.selectedCategories.map( ( cat ) => cat.id )
				: [];

			const latestPostsQuery = pickBy( {
				categories: catIds,
				order: newQuery.order,
				orderby: newQuery.orderBy,
				per_page: newQuery.numberOfItems,
				exclude: currentPost.id,
			}, ( value ) => ! isUndefined( value ) );

			const latestPosts = getEntityRecords( 'postType', 'post', latestPostsQuery );

			return ! isEmpty( latestPosts ) && latestPosts.map( ( post ) => {
				if ( post.featured_media ) {
					const image = getMedia( post.featured_media );
					let url = get(
						image,
						[
							'media_details',
							'sizes',
							'source_url',
						],
						null
					);
					if ( ! url ) {
						url = get( image, 'source_url', null );
					}
					post = {
						...post,
						featured_media_object: post.featured_media && select( 'core' ).getMedia( post.featured_media ),
					};
				}

				if ( ! isEmpty( post.categories ) ) {
					const catQuery = { per_page: -1, context: 'view', include: post.categories };
					const terms = getEntityRecords( 'taxonomy', 'category', catQuery );
					post = {
						...post,
						categories: terms,
					};
				}
				if ( post.author ) {
					const author = getUser( post.author, {
						context: 'view',
					} );
					post = {
						...post,
						authorInfo: author,
					};
				}

				return post;
			} );
		};

		return {
			latestPosts: useUpdatedQueryControls ? updatedQuery() : [],
			useUpdatedQueryControls,
		};
	} ),
	withDispatch( ( dispatch, ownProps ) => {
		const { selectBlock } = dispatch( 'core/block-editor' );

		return {
			selectBlock: () => {
				selectBlock( ownProps.clientId );
			},
		};
	} ),
] )( Edit );
